import React, { useEffect, useRef } from 'react';
import '../styles/AsciiBackground.css';

const CHARS = '01<>[]{}#$%+=*x';
const GLOW_CHARS = new Set('1<>');
const WORDS = ['python', 'terraform', 'java', 'docker', 'react', 'node', 'linux', 'ansible', 'git', 'cloud', 'fastapi', 'spring', 'k8s', 'ssh'];

function rand(min, max) {
  return min + Math.random() * (max - min);
}

/**
 * Full-screen, fixed ASCII animation background.
 * - Digital "rain" of random glyphs falling in columns (cyan / violet)
 * - A scrolling ascii wave along the bottom edge
 * - Faint tech vocabulary drifting upward
 * Pauses when the tab is hidden and respects prefers-reduced-motion.
 */
export default function AsciiBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf = 0;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fontPx = 13;
    const font = `${fontPx}px "JetBrains Mono", "Fira Code", Menlo, Consolas, monospace`;
    const colStep = fontPx;
    const rowStep = Math.round(fontPx * 1.45);

    let columns = [];
    let wordPool = [];

    function init() {
      const cols = Math.max(8, Math.floor(window.innerWidth / colStep));
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(window.innerWidth * dpr);
      canvas.height = Math.round(window.innerHeight * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.font = font;

      columns = [];
      for (let i = 0; i < cols; i++) {
        columns.push({
          x: i * colStep,
          y: rand(-window.innerHeight, window.innerHeight),
          speed: rand(140, 480) * (reducedMotion ? 0.12 : 1),
          length: rand(6, 20),
          color: Math.random() < 0.12 ? '#a78bfa' : Math.random() < 0.3 ? '#67e8f9' : '#22d3ee',
          chars: []
        });
      }

      wordPool = [];
      for (let i = 0; i < 12; i++) {
        wordPool.push({
          text: WORDS[Math.floor(Math.random() * WORDS.length)],
          x: rand(0, window.innerWidth),
          y: rand(0, window.innerHeight),
          speed: rand(6, 16),
          life: rand(2, 8),
          maxLife: 0
        });
        wordPool[i].maxLife = wordPool[i].life;
      }
    }

    function spawnWord() {
      const w = {
        text: WORDS[Math.floor(Math.random() * WORDS.length)],
        x: rand(0, window.innerWidth),
        y: window.innerHeight + rand(10, 140),
        speed: rand(6, 16),
        life: rand(6, 10),
        maxLife: 0
      };
      w.maxLife = w.life;
      wordPool.push(w);
    }

    function draw(now, dt) {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      // ---- Faint tech vocabulary ----
      ctx.font = font;
      const next = [];
      for (const w of wordPool) {
        w.y -= w.speed * dt;
        w.life -= dt;
        if (w.life > 0 && w.y > -fontPx) {
          const fade = Math.max(0, Math.min(w.life / 1.2, 1 - w.life / (w.maxLife - 1.2), 1));
          ctx.fillStyle = `rgba(103, 232, 249, ${0.06 * fade})`;
          ctx.fillText(w.text, w.x, w.y);
          next.push(w);
        }
      }
      wordPool = next;
      if (wordPool.length < 10) spawnWord();

      // ---- Digital rain ----
      const waveBand = 7 * rowStep;
      for (const c of columns) {
        c.y += c.speed * dt;
        if (c.y > window.innerHeight + c.length * rowStep + waveBand) {
          c.y = rand(-window.innerHeight * 0.6, 0);
          c.speed = rand(140, 480) * (reducedMotion ? 0.12 : 1);
          c.length = rand(6, 20);
          c.color = Math.random() < 0.12 ? '#a78bfa' : Math.random() < 0.3 ? '#67e8f9' : '#22d3ee';
        }
        c.chars.push(CHARS[Math.floor(Math.random() * CHARS.length)]);
        if (c.chars.length > c.length + 4) c.chars.shift();
        const trail = Math.min(c.chars.length, c.length);
        for (let j = 0; j < trail; j++) {
          const y = c.y - (trail - j) * rowStep;
          if (y < -rowStep || y > window.innerHeight + rowStep) continue;
          const t = j / trail; // 0 = head, 1 = tail
          let a = 0.55 * t * t - 0.08;
          if (a < 0.015) a = 0.015;
          const ch = c.chars[j % c.chars.length];
          ctx.fillStyle =
            GLOW_CHARS.has(ch) && a > 0.3 ? `rgba(167, 243, 255, ${Math.min(a + 0.25, 0.9)})` : c.color;
          ctx.globalAlpha = a;
          ctx.fillText(ch, c.x, y);
        }
      }
      ctx.globalAlpha = 1;

      // ---- Scrolling wave floor ----
      ctx.font = font;
      const waveChars = '~=^';
      const rows = Math.ceil(waveBand / rowStep);
      const h = window.innerHeight;
      for (let r = 0; r < rows; r++) {
        const y = h - r * rowStep - fontPx;
        const amp = 3.5 - r * 0.28;
        const phase = now * 0.0012 * (1 + r * 0.22);
        let line = '';
        for (let x = 0; x < window.innerWidth + fontPx; x += colStep) {
          const o = Math.round(amp * Math.sin(x * 0.012 + phase));
          let ci = o + r;
          if (ci < 0) ci = 0;
          if (ci < waveChars.length) line += waveChars[ci];
        }
        ctx.fillStyle = r === 0 ? 'rgba(34, 211, 238, 0.4)' : `rgba(148, 163, 184, ${0.14 - r * 0.02})`;
        ctx.globalAlpha = 1;
        ctx.fillText(line, 0, y);
      }
      ctx.globalAlpha = 1;
    }

    init();

    let last = performance.now();
    function frame(now) {
      const dt = Math.min((now - last) / 1000, 0.05);
      last = now;
      if (!document.hidden) draw(now, dt);
      raf = requestAnimationFrame(frame);
    }
    if (reducedMotion) {
      // Static frame: draw once with no per-frame advance
      draw(0, 0);
    } else {
      raf = requestAnimationFrame(frame);
    }

    const onResize = () => init();
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="ascii-background"
      aria-hidden="true"
    />
  );
}
